import { memo, useState, useCallback, useRef } from "react";
import { Link } from "react-router";
import { type Article } from "@/data/articles";
import { FALLBACK_IMAGE, formatRelativeTime } from "@/lib/article";
import { BookmarkButton } from "@/components/BookmarkButton";
import { useQueryClient } from "@tanstack/react-query";
import { fetchArticle } from "@/hooks/useArticlesData";
import { getCategoryTheme } from "@/lib/categoryTheme";
import { CategoryBadge } from "@/components/ui/CategoryBadge";

interface ArticleCardProps {
  article: Article;
  variant?: "overlay" | "standard" | "horizontal" | "compact" | "search-result";
  className?: string;
  showBookmark?: boolean;
  showExcerpt?: boolean;
  priority?: boolean;
}

/**
 * Shared image component with:
 *  - Skeleton background while loading (no blank flash)
 *  - Smooth opacity fade-in once loaded
 *  - One-shot fallback on error (no infinite loop)
 */
export function ArticleImage({
  src,
  alt,
  className,
  priority = false,
  width,
  height,
  sizes,
}: {
  src: string;
  alt: string;
  className?: string;
  priority?: boolean;
  width?: number;
  height?: number;
  sizes?: string;
}) {
  const requestedSrc = src || FALLBACK_IMAGE;
  const [state, setState] = useState(() => ({
    requestedSrc,
    imgSrc: requestedSrc,
    loaded: false,
  }));
  const imgSrc =
    state.requestedSrc === requestedSrc ? state.imgSrc : requestedSrc;
  const loaded = state.requestedSrc === requestedSrc ? state.loaded : false;

  const handleError = useCallback(() => {
    setState({ requestedSrc, imgSrc: FALLBACK_IMAGE, loaded: true });
  }, [requestedSrc]);

  const handleLoad = useCallback(() => {
    setState({ requestedSrc, imgSrc, loaded: true });
  }, [requestedSrc, imgSrc]);

  let srcSet: string | undefined = undefined;
  if (/\/storage\/v1\/object\/public\//.test(imgSrc)) {
    srcSet = [128, 256, 320, 400, 500, 640, 800, 960, 1280]
      .map((w) => {
        const cleanUrl = imgSrc.replace(/^https?:\/\//, "");
        return `https://wsrv.nl/?url=${encodeURIComponent(cleanUrl)}&w=${w}&output=webp ${w}w`;
      })
      .join(", ");
  } else if (/\.s3\.(ap-south-1\.)?amazonaws\.com\//.test(imgSrc)) {
    srcSet = [128, 256, 320, 400, 500, 640, 800, 960, 1280]
      .map((w) => {
        const cleanUrl = imgSrc.replace(/^https?:\/\//, "");
        return `https://wsrv.nl/?url=${encodeURIComponent(cleanUrl)}&w=${w}&output=webp ${w}w`;
      })
      .join(", ");
  }

  return (
    <>
      {/* Skeleton visible while image is loading */}
      {!loaded && (
        <div
          className="absolute inset-0 bg-muted animate-pulse"
          aria-hidden="true"
        />
      )}
      <img
        src={imgSrc}
        srcSet={srcSet}
        sizes={
          sizes || (srcSet
            ? "(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
            : undefined)
        }
        alt={alt}
        className={`${className} transition-opacity duration-500 ${loaded ? "opacity-100" : "opacity-0"}`}
        loading={priority ? "eager" : "lazy"}
        fetchPriority={priority ? "high" : "auto"}
        width={width}
        height={height}
        onLoad={handleLoad}
        onError={handleError}
        decoding="async"
      />
    </>
  );
}

export const ArticleCard = memo(function ArticleCard({
  article,
  variant = "standard",
  className = "",
  showBookmark = true,
  showExcerpt = false,
  priority = false,
}: ArticleCardProps) {
  const queryClient = useQueryClient();
  const cardRef = useRef<HTMLAnchorElement>(null);

  const handleMouseMove = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    cardRef.current.style.setProperty("--mouse-x", `${x}px`);
    cardRef.current.style.setProperty("--mouse-y", `${y}px`);
  };

  const handlePrefetch = useCallback(() => {
    queryClient.prefetchQuery({
      queryKey: ["article", article.slug],
      queryFn: () => fetchArticle(article.slug),
      staleTime: 1000 * 60 * 5,
    });
  }, [queryClient, article.slug]);

  // Category theme provides the canonical gradient badge and accent color
  const categoryTheme = getCategoryTheme(article.category || article.categoryLabel);

  const hoverStyle = {
    "--hover-color": categoryTheme.color,
  } as React.CSSProperties;

  // Only show BookmarkButton for real DB articles (UUID format)
  const isDbArticle =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      article.id,
    );

  /* ── search-result ─────────────────────────────────────────────── */
  if (variant === "search-result") {
    return (
      <Link
        to={`/article/${article.slug}`}
        style={hoverStyle}
        onMouseEnter={handlePrefetch}
        onFocus={handlePrefetch}
        className={`group flex gap-4 p-4 rounded-2xl hover:bg-accent/50 transition-all duration-300 border border-transparent hover:border-[var(--hover-color)] items-center ${className}`}
      >
        <div className="w-16 h-20 md:w-20 md:h-24 bg-muted rounded-lg overflow-hidden flex-shrink-0 relative shadow-sm">
          <ArticleImage
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-105 will-change-transform"
            priority={priority}
          />
        </div>
        <div className="flex-1 min-w-0 flex flex-col gap-1">
          <CategoryBadge
            category={article.category || article.categoryLabel || "general"}
            label={article.categoryLabel}
            variant="solid"
            size="xs"
            showIcon={false}
          />
          <h3 className="text-sm md:text-base font-bold text-foreground leading-tight group-hover:text-[var(--hover-color)] transition-colors duration-200 line-clamp-2">
            {article.title}
          </h3>
          <span className="text-xs text-muted-foreground mt-1 block">
            {article.date}
          </span>
        </div>
      </Link>
    );
  }

  /* ── overlay ───────────────────────────────────────────────────── */
  if (variant === "overlay") {
    return (
      <Link
        to={`/article/${article.slug}`}
        style={hoverStyle}
        onMouseEnter={handlePrefetch}
        onFocus={handlePrefetch}
        className={`group relative block rounded-xl overflow-hidden ${className}`}
      >
        <ArticleImage
          src={article.image}
          alt={article.title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.03] will-change-transform"
          priority={priority}
          width={800}
          height={600}
        />
        {/* Subtle gradient darkens slightly on hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent transition-opacity duration-300 group-hover:from-black/85" />
        <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
          <CategoryBadge
            category={article.category || article.categoryLabel || "general"}
            label={article.categoryLabel}
            variant="glass"
            size="xs"
            showIcon
            className="mb-2 md:mb-3"
          />
          <h2 className="text-sm md:text-lg lg:text-2xl font-bold text-white leading-snug mb-1 md:mb-2 transition-colors duration-300 group-hover:text-[var(--hover-color)]">
            {article.title}
          </h2>
          <span className="text-xs text-white/70 block mt-1">
            {article.date}
          </span>
        </div>
        {showBookmark && isDbArticle && (
          <div
            className="absolute bottom-4 right-4"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <BookmarkButton articleId={article.id} />
          </div>
        )}
      </Link>
    );
  }

  /* ── horizontal ────────────────────────────────────────────────── */
  if (variant === "horizontal") {
    return (
      <Link
        to={`/article/${article.slug}`}
        style={hoverStyle}
        onMouseEnter={handlePrefetch}
        onFocus={handlePrefetch}
        className={`group flex gap-4 items-start ${className}`}
      >
        <div className="w-28 md:w-40 aspect-video rounded-md overflow-hidden flex-shrink-0 relative bg-muted">
          <ArticleImage
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover object-center transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.03] will-change-transform"
            priority={priority}
            width={300}
            height={168}
          />
          <div className="absolute bottom-2 left-2">
            <CategoryBadge
              category={article.category || article.categoryLabel || "general"}
              label={article.categoryLabel}
              variant="solid"
              size="xs"
              showIcon={false}
            />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-foreground leading-snug transition-colors duration-200 group-hover:text-[var(--hover-color)] line-clamp-2">
            {article.title}
          </h3>
          <span className="text-xs text-muted-foreground mt-1 block">
            {article.date}
          </span>
        </div>
        {showBookmark && isDbArticle && (
          <div
            className="flex-shrink-0 mt-1"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <BookmarkButton articleId={article.id} />
          </div>
        )}
      </Link>
    );
  }

  /* ── compact ───────────────────────────────────────────────────── */
  if (variant === "compact") {
    return (
      <Link
        to={`/article/${article.slug}`}
        style={hoverStyle}
        onMouseEnter={handlePrefetch}
        onFocus={handlePrefetch}
        className={`group flex gap-3 p-3 rounded-xl border border-transparent hover:border-[var(--hover-color)] hover:bg-accent/50 transition-all duration-200 items-center ${className}`}
      >
        <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-muted relative shadow-sm">
          <ArticleImage
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.05] will-change-transform"
            priority={priority}
            width={100}
            height={100}
            sizes="100px"
          />
        </div>
        <div className="flex-1 min-w-0 flex flex-col justify-center gap-1">
          <div className="flex items-center justify-between gap-2">
            <span
              className="text-[10px] font-bold uppercase tracking-wider truncate"
              style={{ color: "var(--hover-color)" }}
            >
              {article.categoryLabel}
            </span>
            <span className="text-[10px] text-muted-foreground shrink-0 font-medium">
              {formatRelativeTime(article.publishedAt || article.date)}
            </span>
          </div>
          <h3 className="text-sm font-semibold text-foreground leading-snug transition-colors duration-200 group-hover:text-[var(--hover-color)] line-clamp-2">
            {article.title}
          </h3>
        </div>
      </Link>
    );
  }

  /* ── standard (default) ────────────────────────────────────────── */
  return (
    <Link
      ref={cardRef}
      onMouseMove={handleMouseMove}
      to={`/article/${article.slug}`}
      style={hoverStyle}
      onMouseEnter={handlePrefetch}
      onFocus={handlePrefetch}
      className={`group relative block ${className}`}
    >
      {/* Dynamic Cursor Spotlight Aura Glow */}
      <div
        className="absolute -inset-2 rounded-2xl pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"
        style={{
          background: `radial-gradient(300px circle at var(--mouse-x, 50%) var(--mouse-y, 50%), hsla(var(--primary) / 0.09), transparent 75%)`,
        }}
      />
      {/* Image container — soft lift on hover, no jarring scale */}
      <div className="relative rounded-xl overflow-hidden aspect-video mb-3 shadow-sm transition-shadow duration-300 group-hover:shadow-md bg-muted">
        <ArticleImage
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover object-center transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.03] will-change-transform"
          priority={priority}
          width={800}
          height={450}
        />
        {/* Very subtle darkening overlay on hover */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/8 transition-colors duration-300" />
        <div className="absolute bottom-2 left-2">
          <CategoryBadge
            category={article.category || article.categoryLabel || "general"}
            label={article.categoryLabel}
            variant="glass"
            size="xs"
            showIcon
          />
        </div>
      </div>

      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          {/* Title — smooth color transition, no underline jump */}
          <h3 className="text-sm md:text-base font-bold text-foreground leading-snug transition-colors duration-300 group-hover:text-[var(--hover-color)] mb-1 line-clamp-2">
            {article.title}
          </h3>

          {showExcerpt && (
            <p className="text-sm text-muted-foreground line-clamp-2 mb-1 leading-relaxed">
              {article.excerpt}
            </p>
          )}

          <span className="text-xs text-muted-foreground block mt-1">{article.date}</span>
        </div>

        {showBookmark && isDbArticle && (
          <div
            className="flex-shrink-0"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <BookmarkButton articleId={article.id} />
          </div>
        )}
      </div>
    </Link>
  );
});
